import { Export } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import AndroidShare from '@/assets/images/icons/share-android.svg?react';
import IOSShare from '@/assets/images/icons/share-ios.svg?react';
import type { BatchMessage } from '@/hooks/batch-message/use-get-batch-messages';
import type { Collection } from '@/hooks/collections/use-collections';
import useShare from '@/hooks/use-share';
import useUserPlatform from '@/hooks/use-user-platform';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { getCollectionFileName, getFileBySource, getMessageFileName } from '@/utils/get-file';
import { getSafeMediaUrl } from '@/utils/media-url';
import cn from '@/utils/merge-class-names';
import { canParseAsUrl } from '@/utils/parse-url';

const allowShareMessageType = [
  ConversationMessageContentType.Text,
  ConversationMessageContentType.File,
  ConversationMessageContentType.Image,
  ConversationMessageContentType.Video,
];

const shareData = z.object({
  files: z.instanceof(File).array().optional(),
  url: z.string().optional(),
  title: z.string().optional(),
  text: z.string().optional(),
});

export const NativeShareButton = ({
  isLoading,
  content,
  className,
}: {
  isLoading?: boolean;
  content:
    | { type: 'qrcode'; data: Blob }
    | { type: 'message'; data: ConversationMessageTypeUnion }
    | { type: 'collection'; data: Collection }
    | { type: 'batch-message'; data: BatchMessage }
    | string
    | ShareData
    | undefined;
  className?: string;
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const { isAndroid, isIOS, isEmbedded } = useUserPlatform();
  const { share, isSharing, canShare: canNativeShare, validateShareData } = useShare();

  useEffect(() => {
    if (!content) return;

    (async () => {
      try {
        setIsValidating(true);

        if (typeof content === 'string') {
          if (canParseAsUrl(content)) {
            validateShareData({ url: content });
          } else {
            validateShareData({ text: content });
          }
          return;
        }

        if ('type' in content && 'data' in content) {
          const { type, data } = content;

          switch (type) {
            case 'message':
              if (!allowShareMessageType.includes(data.MsgType) || data.ReadDelete) {
                return;
              }
              if (data.MsgType === ConversationMessageContentType.Text) {
                validateShareData({ text: data.Message });
              } else if (data.MsgType === ConversationMessageContentType.File) {
                const [url, fileName] = data.Message.split('|');
                if (!url) return;
                const isPDF = url.includes('pdf');
                const safeUrl = getSafeMediaUrl(url);
                const file = await getFileBySource(url, fileName ?? getMessageFileName(data));

                // Android do not support file sharing except pdf
                // ISO support file sharing but need to attach with text or url to show Line/WhatApp/Teams etc options
                validateShareData(
                  file && (isIOS ? { title: fileName, files: [file] } : isPDF ? { files: [file] } : { url: safeUrl }),
                );
              } else {
                const fileName = getMessageFileName(data);
                const file = await getFileBySource(data.Message, fileName);
                if (file?.name.split('.').pop() === 'blob') return;
                validateShareData(file && { files: [file] });
              }
              break;

            case 'collection':
              if (!allowShareMessageType.includes(data.Type)) {
                return;
              }
              if (data.Type === ConversationMessageContentType.Text) {
                validateShareData({ text: data.Content });
              } else if (data.Type === ConversationMessageContentType.File) {
                const fileName = getCollectionFileName(data);
                const url = data.SmallPicturePath;

                const isPDF = url.includes('pdf');
                const safeUrl = getSafeMediaUrl(url);
                const file = await getFileBySource(url, fileName);

                // Android do not support file sharing except pdf
                // ISO support file sharing but need to attach with text or url to show Line/WhatApp/Teams etc options
                validateShareData(
                  file && (isIOS ? { title: fileName, files: [file] } : isPDF ? { files: [file] } : { url: safeUrl }),
                );
              } else {
                const fileName = getCollectionFileName(data);
                const file = await getFileBySource(data.Content, fileName);

                if (file?.name.split('.').pop() === 'blob') return;
                validateShareData(file && { files: [file] });
              }
              break;
            case 'qrcode': {
              const fileName = `qrcode.${data.type.split('/').pop()}`;
              const file = new File([data], fileName, { type: data.type });
              validateShareData({ files: [file] });
              break;
            }
            case 'batch-message':
              if (!allowShareMessageType.includes(data.MsgType)) {
                return;
              }
              if (data.MsgType === ConversationMessageContentType.Text) {
                validateShareData({ text: data.Message });
              } else if (data.MsgType === ConversationMessageContentType.File) {
                const [url, fileName] = data.Message.split('|');
                if (!url) return;
                const isPDF = url.includes('pdf');
                const safeUrl = getSafeMediaUrl(url);
                const file = await getFileBySource(url, fileName ?? getMessageFileName(data));

                // Android do not support file sharing except pdf
                // ISO support file sharing but need to attach with text or url to show Line/WhatApp/Teams etc options
                validateShareData(
                  file && (isIOS ? { title: fileName, files: [file] } : isPDF ? { files: [file] } : { url: safeUrl }),
                );
              } else {
                const fileName = getMessageFileName(data);
                const file = await getFileBySource(data.Message, fileName);
                if (file?.name.split('.').pop() === 'blob') return;
                validateShareData(file && { files: [file] });
              }
              break;
          }
        }

        const result = shareData.safeParse(content);

        if (result.success) {
          validateShareData(result.data);
        }
      } catch (error) {
        console.warn('Error in resolveShareContent:', error);
      } finally {
        setIsValidating(false);
      }
    })();
  }, [content]);

  if (isEmbedded) return;

  if (isLoading || isValidating) {
    return (
      <div className={cn('w-6 flex align-middle justify-center !text-secondary', className)}>
        <span className={cn('loading loading-spinner')} />
      </div>
    );
  }

  if (canNativeShare) {
    return (
      <button
        disabled={isSharing}
        type='button'
        className={cn('btn btn-ghost p-0', className)}
        onClick={share}>
        {isAndroid ? (
          <AndroidShare className='size-6' />
        ) : isIOS ? (
          <IOSShare className='size-8' />
        ) : (
          <Export size={26} />
        )}
      </button>
    );
  }
};
